/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {ListMenuContent} from './list-menu-content'
import {Box, Text, useMultiStyleConfig, Slide} from '@chakra-ui/react'
import HeaderDown from '../icons/arrows/HeaderDown'
import HeaderUp from '../icons/arrows/HeaderUp'
import {getContentstackLink} from '../../utils/contentstack-utils'

const ListMenuPopover = ({
    primarycategory,
    categoriesData,
    selectedMenuItem,
    setSelectedMenuItem,
}) => {
    const isOpen = selectedMenuItem === primarycategory.uid

    const styles = useMultiStyleConfig('ListMenu')

    const keyMap = {
        Escape: () => setSelectedMenuItem(null),
        Enter: () =>
            isOpen ? setSelectedMenuItem(null) : setSelectedMenuItem(primarycategory.uid),
    }

    const {url, displayName} = getContentstackLink(primarycategory?.redirects, categoriesData)

    return (
        <Box>
            <Box {...styles.listMenuTriggerContainer}>
                <Box
                    {...styles.listMenuTriggerGroup}
                    onClick={() => setSelectedMenuItem(primarycategory.uid)}
                >
                    <Text
                        {...styles.listMenuTriggerLink}
                        {...(isOpen ? styles.listMenuTriggerLinkActive : {})}
                        {...{name: displayName + ' __'}}
                    >
                        {displayName}
                    </Text>
                    {/* this is a bold duplicate of menu element text to reserve the 
                    correct space and stop layout shift when normal -> bold on hover*/}
                    <Text aria-hidden="true" {...styles.listMenuTriggerLinkDuplicate}>
                        {displayName}
                    </Text>
                    {isOpen ? (
                        <HeaderUp
                            onKeyDown={(e) => {
                                keyMap[e.key]?.(e)
                            }}
                            tabIndex="0"
                            boxSize="iconsSize.small"
                        />
                    ) : (
                        <HeaderDown
                            onKeyDown={(e) => {
                                keyMap[e.key]?.(e)
                            }}
                            tabIndex="0"
                            boxSize="iconsSize.small"
                        />
                    )}
                </Box>
            </Box>
            {isOpen && (
                <>
                    <Box
                        {...styles.popoverOverlay}
                        onMouseEnter={() => setSelectedMenuItem(null)}
                    />
                    <Slide
                        direction="top"
                        in={isOpen}
                        style={{position: 'absolute', top: '100%', zIndex: -1}}
                    >
                        <Box {...styles.listMenuContent}>
                            <ListMenuContent
                                secondLevel={primarycategory?.second_level}
                                categoriesData={categoriesData}
                                categoryUrl={url}
                            />
                        </Box>
                    </Slide>
                </>
            )}
        </Box>
    )
}

ListMenuPopover.propTypes = {
    categoriesData: PropTypes.object,
    primarycategory: PropTypes.object,
    selectedMenuItem: PropTypes.string,
    setSelectedMenuItem: PropTypes.func,
}

export {ListMenuPopover}
